'use client'

import {
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import { ThemeContext } from './context-base'
import type { ThemeContextValue } from './types'
import { getEffectiveThemePrice, getThemeById, getThemeVars, THEME_PRESETS, type ThemePreset } from '@/lib/themes'
import { applyRuntimeThemeEffects, clearRuntimeThemeEffects } from '@/lib/theme-runtime-effects'
import { loadThemeFonts } from '@/lib/fonts/font-loader'

export interface ThemeState {
  activeThemeId: string
  ownedThemeIds: string[]
  previewThemeId: string | null
}

export interface ThemeContextValue {
  activeTheme: ThemePreset
  renderedTheme: ThemePreset
  ownedThemeIds: string[]
  previewThemeId: string | null
  isPreviewActive: boolean
  hydrated: boolean
  isThemeChanging: boolean

  setActiveTheme: (id: string) => void
  purchaseTheme: (id: string) => boolean
  isThemeOwned: (id: string) => boolean
  syncOwnedThemesFromInventory: (ownedThemeIds: string[], preferredActiveThemeId?: string | null) => void

  startPreview: (themeId: string) => void
  stopPreview: () => void
}

const DEFAULT_THEME_STATE: ThemeState = {
  activeThemeId: 'core-protocol',
  ownedThemeIds: ['core-protocol'],
  previewThemeId: null,
}

const VALID_THEME_IDS = new Set(THEME_PRESETS.map((theme) => theme.id))

function normalizeOwnedThemeIds(ids: string[]) {
  return Array.from(new Set(['core-protocol', ...ids.filter((id) => VALID_THEME_IDS.has(id))])).sort((a, b) => a.localeCompare(b))
}

function loadThemeState(): ThemeState {
  if (typeof window === 'undefined') return DEFAULT_THEME_STATE

  try {
    localStorage.removeItem('habitpack-theme')
    localStorage.removeItem('habitpack-theme-v2')
    localStorage.removeItem('habitpack-theme-v3')

    const saved = localStorage.getItem('habitpack-theme-v4')
    if (!saved) return DEFAULT_THEME_STATE

    const parsed = JSON.parse(saved) as Partial<ThemeState>
    const ownedThemeIds = normalizeOwnedThemeIds(parsed.ownedThemeIds ?? [])
    const activeThemeId =
      typeof parsed.activeThemeId === 'string' && ownedThemeIds.includes(parsed.activeThemeId)
        ? parsed.activeThemeId
        : 'core-protocol'

    return {
      activeThemeId,
      ownedThemeIds,
      previewThemeId: null,
    }
  } catch {
    return DEFAULT_THEME_STATE
  }
}

function saveThemeState(state: ThemeState) {
  if (typeof window === 'undefined') return
  try {
    const activeTheme = getThemeById(state.activeThemeId)
    const vars = activeTheme ? getThemeVars(activeTheme) : null
    const shellClasses = activeTheme?.baseStyle.shellClasses ?? ''

    localStorage.setItem(
      'habitpack-theme-v4',
      JSON.stringify({
        activeThemeId: state.activeThemeId,
        ownedThemeIds: state.ownedThemeIds,
        boot: {
          vars,
          shellClasses,
          category: activeTheme?.category ?? 'system-core',
          highContrast: document.documentElement.dataset.highContrast === 'true',
        },
      }),
    )
  } catch {
    // ignore persistence errors
  }
}

function applyTheme(theme: ThemePreset, highContrast?: boolean) {
  const root = document.documentElement
  const main = document.querySelector('[data-habitpack-shell]')
  const vars = getThemeVars(theme)

  for (const [key, value] of Object.entries(vars)) {
    root.style.setProperty(key, value)
  }

  if (main) {
    const existing = main.className.split(' ')
    const cleaned = existing.filter((c) => !c.startsWith('theme-') && !c.startsWith('font-'))
    const newClasses = theme.baseStyle.shellClasses.split(' ').filter(Boolean)
    main.className = [...cleaned, ...newClasses].join(' ')
  }

  root.dataset.activeTheme = theme.id
  root.dataset.activeThemeCategory = theme.category
  
  if (highContrast) {
    root.dataset.highContrast = 'true'
  } else {
    delete root.dataset.highContrast
  }

  applyRuntimeThemeEffects(theme.id)
  loadThemeFonts(theme.id)
}

function clearThemeVars() {
  const root = document.documentElement
  const allKeys = new Set<string>()

  for (const preset of THEME_PRESETS) {
    const vars = getThemeVars(preset)
    for (const key of Object.keys(vars)) allKeys.add(key)
  }

  for (const key of allKeys) {
    root.style.removeProperty(key)
  }

  clearRuntimeThemeEffects()
  delete root.dataset.activeTheme
  delete root.dataset.activeThemeCategory
  delete root.dataset.highContrast
}



interface ThemeProviderProps {
  children: ReactNode
  onPurchaseAttempt?: (themeId: string, price: number) => boolean
  highContrast?: boolean
}

function areSameIds(left: string[], right: string[]) {
  if (left.length !== right.length) return false
  for (let i = 0; i < left.length; i += 1) {
    if (left[i] !== right[i]) return false
  }
  return true
}

export function ThemeProvider({ children, onPurchaseAttempt, highContrast }: ThemeProviderProps): React.JSX.Element {
  const [state, setState] = useState<ThemeState>(DEFAULT_THEME_STATE)
  const [hydrated, setHydrated] = useState(false)
  const [isThemeChanging, setIsThemeChanging] = useState(false)

  useLayoutEffect(() => {
    const saved = loadThemeState()
    setState(saved)
    setHydrated(true)
    // Sync boot data to localStorage for early injection on next reload
    saveThemeState(saved)
  }, [])

  useLayoutEffect(() => {
    if (!hydrated) return

    clearThemeVars()
    const themeId = state.previewThemeId ?? state.activeThemeId
    const theme = getThemeById(themeId)
    if (theme) applyTheme(theme, highContrast)
  }, [hydrated, state.activeThemeId, state.previewThemeId, highContrast])

  const activeTheme = getThemeById(state.activeThemeId) ?? THEME_PRESETS[0]
  const renderedTheme = getThemeById(state.previewThemeId ?? state.activeThemeId) ?? THEME_PRESETS[0]

  const setActiveTheme = useCallback(async (id: string) => {
    if (!state.ownedThemeIds.includes(id)) return

    setIsThemeChanging(true)

    setState((prev) => {
      const next: ThemeState = { ...prev, activeThemeId: id, previewThemeId: null }
      saveThemeState(next)
      return next
    })

    // Wait for CSS variables to fully apply before closing modal
    await new Promise((resolve) => setTimeout(resolve, 300))
    setIsThemeChanging(false)
  }, [state.ownedThemeIds])

  const purchaseTheme = useCallback(
    (id: string) => {
      const theme = getThemeById(id)
      if (!theme) return false

      const price = getEffectiveThemePrice(theme)
      const allowed = onPurchaseAttempt?.(id, price) ?? false
      if (!allowed) return false

      setState((prev) => {
        if (prev.ownedThemeIds.includes(id)) return prev
        const nextOwned = normalizeOwnedThemeIds([...prev.ownedThemeIds, id])
        const next: ThemeState = {
          ...prev,
          ownedThemeIds: nextOwned,
          previewThemeId: null,
        }
        saveThemeState(next)
        return next
      })

      return true
    },
    [onPurchaseAttempt],
  )

  const syncOwnedThemesFromInventory = useCallback((ownedThemeIds: string[], preferredActiveThemeId?: string | null) => {
    setState((prev) => {
      const nextOwned = normalizeOwnedThemeIds(ownedThemeIds)

      const preferredThemeId =
        typeof preferredActiveThemeId === 'string' && nextOwned.includes(preferredActiveThemeId)
          ? preferredActiveThemeId
          : null

      const nextActiveThemeId = preferredThemeId ?? (nextOwned.includes(prev.activeThemeId) ? prev.activeThemeId : 'core-protocol')

      const nextPreviewThemeId = prev.previewThemeId && getThemeById(prev.previewThemeId)
        ? prev.previewThemeId
        : null

      if (
        prev.activeThemeId === nextActiveThemeId &&
        prev.previewThemeId === nextPreviewThemeId &&
        areSameIds(prev.ownedThemeIds, nextOwned)
      ) {
        return prev
      }

      const next: ThemeState = {
        activeThemeId: nextActiveThemeId,
        ownedThemeIds: nextOwned,
        previewThemeId: nextPreviewThemeId,
      }
      saveThemeState(next)
      return next
    })
  }, [])

  const isThemeOwned = useCallback((id: string) => state.ownedThemeIds.includes(id), [state.ownedThemeIds])

  const startPreview = useCallback((themeId: string) => {
    setState((prev) => ({ ...prev, previewThemeId: themeId }))
  }, [])

  const stopPreview = useCallback(() => {
    setState((prev) => {
      if (!prev.previewThemeId) return prev
      clearThemeVars()
      const theme = getThemeById(prev.activeThemeId)
      if (theme) applyTheme(theme)
      return { ...prev, previewThemeId: null }
    })
  }, [])

  const value = useMemo<ThemeContextValue>(
    () => ({
      activeTheme,
      renderedTheme,
      ownedThemeIds: state.ownedThemeIds,
      previewThemeId: state.previewThemeId,
      isPreviewActive: state.previewThemeId !== null,
      hydrated,
      isThemeChanging,
      setActiveTheme,
      purchaseTheme,
      isThemeOwned,
      syncOwnedThemesFromInventory,
      startPreview,
      stopPreview,
    }),
    [
      activeTheme,
      renderedTheme,
      state.ownedThemeIds,
      state.previewThemeId,
      hydrated,
      isThemeChanging,
      setActiveTheme,
      purchaseTheme,
      isThemeOwned,
      syncOwnedThemesFromInventory,
      startPreview,
      stopPreview,
    ],
  )

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
}

export function ScopedThemeProvider({ themeId, children, className }: { themeId: string; children: ReactNode; className?: string }): React.JSX.Element {
  const theme = getThemeById(themeId) ?? THEME_PRESETS[0]
  const vars = useMemo(() => getThemeVars(theme), [theme])

  const contextValue = useMemo<ThemeContextValue>(() => ({
    activeTheme: theme,
    renderedTheme: theme,
    ownedThemeIds: [themeId],
    previewThemeId: null,
    isPreviewActive: false,
    hydrated: true,
    isThemeChanging: false,
    setActiveTheme: () => {},
    purchaseTheme: () => false,
    isThemeOwned: () => true,
    syncOwnedThemesFromInventory: () => {},
    startPreview: () => {},
    stopPreview: () => {},
  }), [theme, themeId])

  return (
    <ThemeContext.Provider value={contextValue}>
      <div 
        data-scoped-theme={themeId}
        className={className}
        style={vars as React.CSSProperties}
      >
        {children}
      </div>
    </ThemeContext.Provider>
  )
}
